import PageLoading, { type PageLoadingVariant } from '@/shared/ui/page-loading'
import type { SkeletonKind } from '@/shared/ui/ui-skeleton'
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react'
import { Suspense } from 'react'

/** Một dòng của bảng route. `menuKey` chỉ là dữ liệu khai báo, guard không đọc (R-154). */
export interface RouteEntry {
  path: string
  page: LazyExoticComponent<ComponentType<never>>
  props?: object
  menuKey?: string
  /** Hình khung giả của fallback khi tải trang (route sau đăng nhập). Mặc định 'table'. */
  skeletonKind?: SkeletonKind
}

/** Giữ kiểm tra kiểu giữa `page` và `props` tại chỗ khai, rồi nới về RouteEntry để gom vào mảng. */
export const defineEntry = <P extends object>(entry: {
  path: string
  page: LazyExoticComponent<ComponentType<P>>
  props?: P
  menuKey?: string
  skeletonKind?: SkeletonKind
}): RouteEntry => entry as RouteEntry

export function LazyLoadingComponent({
  children,
  variant = 'private',
  skeletonKind,
}: {
  children: ReactNode
  variant?: PageLoadingVariant
  skeletonKind?: SkeletonKind
}) {
  return (
    <Suspense fallback={<PageLoading fullscreen variant={variant} skeletonKind={skeletonKind} />}>
      {children}
    </Suspense>
  )
}

const buildRoute = (
  { path, page, props, skeletonKind }: RouteEntry,
  variant: PageLoadingVariant
) => {
  const Page = page as LazyExoticComponent<ComponentType<object>>
  return {
    path,
    element: (
      <LazyLoadingComponent variant={variant} skeletonKind={skeletonKind}>
        <Page {...props} />
      </LazyLoadingComponent>
    ),
  }
}

/** Route sau đăng nhập: fallback là skeleton. */
export const toRoute = (entry: RouteEntry) => buildRoute(entry, 'private')

/** Route công khai: fallback là spinner AntD. */
export const toPublicRoute = (entry: RouteEntry) => buildRoute(entry, 'public')
